/**
 * three.js シーン管理。
 *
 * - shared/geometry.ts が生成したメッシュデータを BufferGeometry に載せる
 * - 同じ entity を shared/physics.ts の当たり判定ワールドにも登録する
 *   （見た目と当たり判定が食い違わないよう、ここで一元管理する）
 * - 一人称カメラ（クロスヘア中心のレイキャスト）とプレビュー表示
 * - G-buffer + ReSTIR DI + 解析 SDF で点光源をライティングする
 * - ReSTIR GI で拡散バウンス（前フレームの HDR を二次放射に使う）
 * - 太陽光の近距離影は SDF、遠距離は CSM shadow map
 * - 十字の先にピントを合わせる被写界深度（手前と奥のピンボケ）
 */

import * as THREE from 'three';
import {
  entityDepthBias,
  entityDrawRank,
  entityRenderOrder,
  type BlockPose,
} from '../shared/block-lattice';
import type { BlockTextureDefinition } from '../shared/block-textures';
import {
  braceAsSlab,
  braceLogicalSegment,
  buildBlockMesh,
  buildBraceMesh,
  buildEntityMesh,
  entityCentroid,
  isFillBrace,
  isPathBrace,
  planeLogicalSegment,
  type MeshData,
} from '../shared/geometry';
import { braceSnapFrameLines, type BraceSnapLine } from '../shared/brace-snap';
import { planeSnapFrameLines } from '../shared/plane-snap';
import { resolveObjectDefinition } from '../shared/object-catalog';
import { objectWorldAabb, objectWorldPoint } from '../shared/object-pose';
import type { Entity, PlaneGeometry, PointLightComponent, Vec3 } from '../shared/types';
import {
  CollisionWorld,
  entityCollisionBoxes,
  unionBounds,
  type Aabb,
} from '../shared/physics';
import {
  MATERIAL_PALETTE,
  isEmissiveMaterial,
  isTransparentMaterial,
  isWaterMaterial,
  materialMapUrls,
  resolveMaterial,
  skipsSdfOcclusion,
  surfaceEmissive,
  type PaletteEntry,
} from '../shared/materials';
import {
  cellCorners,
  intersectGridPlane,
  type GridCell,
  type GridSide,
  type ViewGridState,
} from '../shared/view-grid';
import { paintBlockTexture } from './block-texture-generator';
import { Environment } from './environment';
import {
  GBuffer,
  LAYER_LIT,
  LAYER_OVERLAY,
  LAYER_OVERLAY_LINES,
  LAYER_TRANSPARENT,
  setObjectLayer,
} from './gbuffer';
import { RestirSdfLighting } from './restir-sdf-lighting';
import { RestirGi } from './restir-gi';
import { VoxelSdfGpu } from './voxel-sdf';
import { AnalyticSdfGpu } from './analytic-sdf';
import { volumeFromCenter } from '../shared/voxel-sdf';
import { CollisionDebugDraw } from './collision-debug';
import { ViewGridOverlay } from './view-grid';
import {
  packPointLightsStable,
  selectNearbyPointLights,
  type PointLightSource,
} from '../shared/point-light-budget';
import { Bloom } from './bloom';
import { DepthOfField } from './depth-of-field';
import { FullScreenQuad } from 'three/addons/postprocessing/Pass.js';
import { createWaterMaterial, updateWaterMaterial } from './water';
import {
  DEFAULT_GRAPHICS_QUALITY,
  GRAPHICS_PRESETS,
  GROUND_DRAW_DISTANCE,
  type GraphicsQuality,
  type GraphicsQualityId,
  resolveGraphicsQuality,
} from './graphics-quality';
import { GpuPassTimer, timePass, timePassValue } from './gpu-pass-timer';

const GUIDE_MARKER_SIZE = 0.16;
const DESTROY_HIGHLIGHT_COLOR = 0xff3b3b;
const GRID_HIGHLIGHT_COLOR = 0x3ddc6a;
const OUTLINE_DARK = 0x050505;
const OUTLINE_LIGHT = 0xffffff;

function hexLuminance(color: number): number {
  const r = ((color >> 16) & 255) / 255;
  const g = ((color >> 8) & 255) / 255;
  const b = (color & 255) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrastOutlineFromLuminance(luminance: number): number {
  return luminance >= 0.55 ? OUTLINE_DARK : OUTLINE_LIGHT;
}

const TEXTURE_SAMPLE_SIZE = 16;

function sampleTextureLuminance(texture: THREE.Texture): number | null {
  const image = texture.image as HTMLImageElement | HTMLCanvasElement | ImageBitmap | undefined;
  if (!image) return null;
  if ('complete' in image && !image.complete) return null;
  const width = 'width' in image ? image.width : 0;
  const height = 'height' in image ? image.height : 0;
  if (!width || !height) return null;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = TEXTURE_SAMPLE_SIZE;
    canvas.height = TEXTURE_SAMPLE_SIZE;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(image, 0, 0, TEXTURE_SAMPLE_SIZE, TEXTURE_SAMPLE_SIZE);
    const pixels = ctx.getImageData(0, 0, TEXTURE_SAMPLE_SIZE, TEXTURE_SAMPLE_SIZE).data;
    let sum = 0;
    let count = 0;
    for (let i = 0; i < pixels.length; i += 4) {
      const alpha = pixels[i + 3] / 255;
      if (alpha < 0.08) continue;
      sum += hexLuminance((pixels[i] << 16) | (pixels[i + 1] << 8) | pixels[i + 2]);
      count += 1;
    }
    return count > 0 ? sum / count : null;
  } catch {
    return null;
  }
}

export interface PickResult {
  point: Vec3;
  normal: Vec3;
  entityId: string | null;
  gridCell?: GridCell;
  gridSide?: GridSide;
  distance: number;
}

export function toBufferGeometry(data: MeshData): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  const uv = new THREE.BufferAttribute(data.uvs, 2);
  geometry.setAttribute('position', new THREE.BufferAttribute(data.positions, 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(data.normals, 3));
  geometry.setAttribute('uv', uv);
  geometry.setAttribute('uv2', uv.clone());
  geometry.setIndex(new THREE.BufferAttribute(data.indices, 1));
  if (data.groups) {
    for (const group of data.groups) {
      geometry.addGroup(group.start, group.count, group.materialIndex);
    }
  }
  if (data.indices.length >= 3) geometry.computeTangents();
  geometry.computeBoundingSphere();
  geometry.computeBoundingBox();
  return geometry;
}

function worldNormalFromHit(hit: THREE.Intersection, target: THREE.Vector3): THREE.Vector3 {
  if (hit.face) target.copy(hit.face.normal);
  else if (hit.normal) target.copy(hit.normal);
  else return target.set(0, 1, 0);
  target.transformDirection(hit.object.matrixWorld);
  return target.normalize();
}

function highlightTargetKey(
  entityId: string | null,
  gridCell?: GridCell,
  grid?: ViewGridState,
  segmentIndex?: number,
  color?: number
): string | null {
  const tint = color ?? 0;
  if (gridCell && grid) return `grid:${grid.y}:${grid.yaw}:${gridCell.i},${gridCell.j}:c:${tint}`;
  if (entityId && segmentIndex !== undefined) return `entity:${entityId}:seg:${segmentIndex}:c:${tint}`;
  if (entityId) return `entity:${entityId}:c:${tint}`;
  return null;
}

function previewCacheKey(
  entity: Entity | null,
  pose?: BlockPose,
  color?: number,
  style: 'solid' | 'frame' = 'solid'
): string {
  if (!entity && !pose) return '';
  return JSON.stringify({
    type: entity?.type ?? null,
    definitionId: entity?.type === 'object' ? entity.definitionId : null,
    material: entity?.material ?? null,
    geometry: entity?.geometry ?? null,
    components: entity?.components ?? null,
    pose: pose ?? null,
    color: color ?? null,
    style,
  });
}

function previewOverlayKey(overlays: readonly Entity[], color?: number): string {
  if (overlays.length === 0) return '';
  return JSON.stringify({
    overlays: overlays.map((entity) => ({
      type: entity.type,
      definitionId: entity.type === 'object' ? entity.definitionId : null,
      material: entity.material,
      geometry: entity.geometry,
      components: entity.components ?? null,
    })),
    color: color ?? null,
  });
}

function guideCacheKey(points: Vec3[], cursor: Vec3 | null, closed: boolean, markerSize: number): string {
  return JSON.stringify({ points, cursor, closed, markerSize });
}

/** 見た目と当たり判定に効く欄だけ比較する。owner / 時刻はサーバーエコーで変わる。 */
function entityVisualEqual(a: Entity, b: Entity): boolean {
  return (
    a.type === b.type &&
    a.material === b.material &&
    (a.type !== 'object' || b.type !== 'object' || a.definitionId === b.definitionId) &&
    JSON.stringify(a.geometry) === JSON.stringify(b.geometry) &&
    JSON.stringify(a.components ?? null) === JSON.stringify(b.components ?? null)
  );
}

function snapFocusKey(focus: Vec3 | undefined, snap: number): string {
  if (!focus || !(snap > 0)) return '';
  return `:${Math.round(focus.x / snap)},${Math.round(focus.y / snap)},${Math.round(focus.z / snap)}`;
}

function snapFrameGeometry(lines: readonly BraceSnapLine[]): THREE.BufferGeometry {
  const positions = new Float32Array(lines.length * 6);
  for (let index = 0; index < lines.length; index++) {
    const { a, b } = lines[index];
    const offset = index * 6;
    positions[offset] = a.x;
    positions[offset + 1] = a.y;
    positions[offset + 2] = a.z;
    positions[offset + 3] = b.x;
    positions[offset + 4] = b.y;
    positions[offset + 5] = b.z;
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  return geometry;
}

function gridCellGeometry(cell: GridCell, grid: ViewGridState): THREE.BufferGeometry {
  const corners = cellCorners(cell, grid);
  const positions = new Float32Array([
    corners[0].x, corners[0].y, corners[0].z, corners[1].x, corners[1].y, corners[1].z,
    corners[1].x, corners[1].y, corners[1].z, corners[2].x, corners[2].y, corners[2].z,
    corners[2].x, corners[2].y, corners[2].z, corners[3].x, corners[3].y, corners[3].z,
    corners[3].x, corners[3].y, corners[3].z, corners[0].x, corners[0].y, corners[0].z,
  ]);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  return geometry;
}

/** ブロックのヨー格子で UV を切り、15°刻みの向きでもテクスチャが面に沿うようにする。 */
function applyWorldTileSize(material: THREE.Material, tileSize: number): void {
  const previous = material.onBeforeCompile;
  material.onBeforeCompile = (shader, renderer) => {
    previous.call(material, shader, renderer);
    shader.uniforms.uvTileSize = { value: tileSize };
    shader.vertexShader = `uniform float uvTileSize;\n${shader.vertexShader}`.replace(
      '#include <uv_vertex>',
      `#include <uv_vertex>
#ifdef USE_MAP
{
  vec4 worldPos = vec4(position, 1.0);
  vec3 worldN = normal;
  mat3 rot = mat3(modelMatrix);
#ifdef USE_INSTANCING
  worldPos = instanceMatrix * worldPos;
  worldN = mat3(instanceMatrix) * worldN;
  rot = rot * mat3(instanceMatrix);
#endif
  worldPos = modelMatrix * worldPos;
  worldN = normalize(mat3(modelMatrix) * worldN);
  vec3 axisX = normalize(vec3(rot[0].x, 0.0, rot[0].z));
  vec3 axisZ = normalize(vec3(rot[2].x, 0.0, rot[2].z));
  vec3 latticePos = vec3(
    dot(vec3(worldPos.x, 0.0, worldPos.z), axisX),
    worldPos.y,
    dot(vec3(worldPos.x, 0.0, worldPos.z), axisZ)
  );
  vec3 latticeN = normalize(vec3(
    dot(vec3(worldN.x, 0.0, worldN.z), axisX),
    worldN.y,
    dot(vec3(worldN.x, 0.0, worldN.z), axisZ)
  ));
  vec3 an = abs(latticeN);
  vec2 worldUv;
  if (an.y >= an.x && an.y >= an.z) {
    worldUv = vec2(latticePos.x, latticeN.y >= 0.0 ? -latticePos.z : latticePos.z);
  } else if (an.x >= an.z) {
    worldUv = vec2(latticeN.x >= 0.0 ? -latticePos.z : latticePos.z, latticePos.y);
  } else {
    worldUv = vec2(latticeN.z >= 0.0 ? latticePos.x : -latticePos.x, latticePos.y);
  }
  worldUv /= uvTileSize;
  vMapUv = (mapTransform * vec3(worldUv, 1.0)).xy;
#ifdef USE_NORMALMAP
  vNormalMapUv = (normalMapTransform * vec3(worldUv, 1.0)).xy;
#endif
#ifdef USE_ROUGHNESSMAP
  vRoughnessMapUv = (roughnessMapTransform * vec3(worldUv, 1.0)).xy;
#endif
#ifdef USE_METALNESSMAP
  vMetalnessMapUv = (metalnessMapTransform * vec3(worldUv, 1.0)).xy;
#endif
#ifdef USE_AOMAP
  vAoMapUv = (aoMapTransform * vec3(worldUv, 1.0)).xy;
#endif
}
#endif
`
    );
  };
  const previousKey = material.customProgramCacheKey.bind(material);
  material.customProgramCacheKey = () => `${previousKey()}|yaw-uv:${tileSize}`;
}

/**
 * ブロックらしい 16x16 のドットテクスチャを作る。
 * 明度のゆらぎとタイル境界の陰影だけを持つグレースケールで、
 * 実際の色はマテリアルの color 側で乗算する。
 */
function createBlockTexture(seed: number): THREE.Texture {
  const size = 16;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;

  // 決定的な擬似乱数（マテリアルごとに違う模様にする）
  let state = seed * 9301 + 49297;
  const random = (): number => {
    state = (state * 9301 + 49297) % 233280;
    return state / 233280;
  };

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const shade = 0.86 + random() * 0.14;
      const value = Math.round(shade * 255);
      ctx.fillStyle = `rgb(${value}, ${value}, ${value})`;
      ctx.fillRect(x, y, 1, 1);
    }
  }
  // タイルの境界を少し暗くしてブロックの輪郭を出す
  ctx.fillStyle = 'rgba(0, 0, 0, 0.16)';
  ctx.fillRect(0, 0, size, 1);
  ctx.fillRect(0, 0, 1, size);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.12)';
  ctx.fillRect(0, size - 1, size, 1);
  ctx.fillRect(size - 1, 0, 1, size);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestMipmapLinearFilter;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  return texture;
}

const COPY_VERT = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

function makeColorTarget(): THREE.WebGLRenderTarget {
  const target = new THREE.WebGLRenderTarget(1, 1, {
    type: THREE.HalfFloatType,
    depthBuffer: false,
  });
  target.texture.colorSpace = THREE.NoColorSpace;
  return target;
}

function makeDepthTexture(): THREE.DepthTexture {
  const depth = new THREE.DepthTexture(1, 1);
  depth.format = THREE.DepthFormat;
  depth.type = THREE.UnsignedIntType;
  depth.minFilter = THREE.NearestFilter;
  depth.magFilter = THREE.NearestFilter;
  return depth;
}

export class WorldScene {
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly renderer: THREE.WebGLRenderer;
  /** 見た目と同期した当たり判定ワールド */
  readonly collision = new CollisionWorld();
  /** 通常描画の代わりにコライダのワイヤーフレームだけを出す。 */
  collisionDebug = false;
  private readonly collisionDebugDraw = new CollisionDebugDraw();
  readonly environment: Environment;
  readonly depthOfField: DepthOfField;
  readonly gbuffer: GBuffer;
  readonly restir: RestirSdfLighting;
  readonly restirGi: RestirGi;
  readonly voxelSdf: VoxelSdfGpu;
  readonly analyticSdf: AnalyticSdfGpu;
  readonly bloom: Bloom;

  private readonly entityGroup = new THREE.Group();
  private readonly overlayGroup = new THREE.Group();
  private readonly meshes = new Map<string, THREE.Mesh>();
  private readonly entities = new Map<string, Entity>();
  private readonly materials = new Map<string, THREE.Material>();
  private readonly textures = new Set<THREE.Texture>();
  private readonly materialMaps = new Map<string, THREE.Texture>();
  private readonly pbrMaps = new Map<string, THREE.Texture[]>();
  private readonly textureLuminance = new Map<string, number>();
  private readonly textureLoader = new THREE.TextureLoader();
  private readonly blockTextures = new Map<string, BlockTextureDefinition>();
  private readonly raycaster = new THREE.Raycaster();
  /** Entity ごとの点光源データ。描画時に視錐台へ届くものだけ ReSTIR へ載せる。 */
  private readonly lightsByEntity = new Map<string, PointLightSource[]>();
  private activeLights: PointLightSource[] = [];
  private activeLightKeys = new Set<string>();
  readonly viewGrid = new ViewGridOverlay();

  private highlight!: THREE.LineSegments;
  private highlightKey: string | null = null;
  private previewMesh: THREE.Mesh | THREE.LineSegments | null = null;
  private previewOverlays: THREE.Mesh[] = [];
  private previewKey: string | undefined;
  private previewOverlayKey: string | undefined;
  private previewMaterial: THREE.MeshBasicMaterial | null = null;
  private previewOverlayMaterial: THREE.MeshBasicMaterial | null = null;
  private previewFrameMaterial: THREE.LineBasicMaterial | null = null;
  private readonly previewSize = new THREE.Vector3();
  private guideLine: THREE.Line | null = null;
  private guideKey: string | undefined;
  private readonly guideBox = new THREE.BoxGeometry(GUIDE_MARKER_SIZE, GUIDE_MARKER_SIZE, GUIDE_MARKER_SIZE);
  private readonly guidePointMaterial = new THREE.MeshBasicMaterial({
    color: 0xffd43b,
    depthTest: false,
    toneMapped: false,
  });
  private readonly guideCursorMaterial = new THREE.MeshBasicMaterial({
    color: 0xffec99,
    depthTest: false,
    toneMapped: false,
  });
  private readonly guideLineMaterial = new THREE.LineBasicMaterial({
    color: 0xffd43b,
    depthTest: false,
    toneMapped: false,
  });
  private guideMarkers: THREE.Mesh[] = [];
  private readonly pickOrigin = new THREE.Vector3();
  private readonly pickDirection = new THREE.Vector3();
  private readonly pickNormal = new THREE.Vector3();
  private readonly pickOriginVec: Vec3 = { x: 0, y: 0, z: 0 };
  private readonly pickDirectionVec: Vec3 = { x: 0, y: 0, z: 0 };
  private readonly lightFrustum = new THREE.Frustum();
  private readonly lightFrustumMatrix = new THREE.Matrix4();
  private readonly lightSphere = new THREE.Sphere();
  private preferredQuality: GraphicsQuality = GRAPHICS_PRESETS[DEFAULT_GRAPHICS_QUALITY];
  private quality: GraphicsQuality = GRAPHICS_PRESETS[DEFAULT_GRAPHICS_QUALITY];
  /** メニュー表示中は低品質＋SDF / ReSTIR を切る。設定値自体は preferredQuality に残す。 */
  private menuLite = false;
  private lastRenderElapsed = 0;
  private readonly sdfSkipIds = new Set<string>();
  private readonly sdfBakeSkipIds = new Set<string>();
  private readonly lit: THREE.WebGLRenderTarget;
  private readonly composite: THREE.WebGLRenderTarget;
  private readonly copyColorQuad: FullScreenQuad;
  private readonly copyDepthQuad: FullScreenQuad;
  private readonly copyColorUniforms = { tDiffuse: { value: null as THREE.Texture | null } };
  private readonly copyDepthUniforms = { tDepth: { value: null as THREE.Texture | null } };
  private occluderBoxes: Aabb[] = [];
  private waterMaterial: THREE.ShaderMaterial | null = null;
  private readonly drawingSize = new THREE.Vector2();
  private passTimer: GpuPassTimer | null = null;
  private passTimingEnabled = false;

  constructor(canvas: HTMLCanvasElement, initialQuality?: GraphicsQualityId | GraphicsQuality) {
    this.preferredQuality =
      typeof initialQuality === 'string' || initialQuality === undefined
        ? resolveGraphicsQuality(initialQuality)
        : initialQuality;
    this.quality = this.preferredQuality;

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: this.quality.id !== 'low',
    });
    this.applyPixelRatio();

    // マイクラと同じくらいの画角。near を小さくして壁際でも抜けにくくする。
    this.camera = new THREE.PerspectiveCamera(70, 1, 0.05, 1200);
    this.raycaster.layers.enableAll();

    this.environment = new Environment(this.scene, this.camera, this.renderer, this.quality);
    this.gbuffer = new GBuffer();
    this.voxelSdf = new VoxelSdfGpu(this.quality);
    this.analyticSdf = new AnalyticSdfGpu();
    this.restir = new RestirSdfLighting(this.renderer);
    this.restir.applyQuality(this.quality);
    this.restirGi = new RestirGi(this.renderer);
    this.restirGi.applyQuality(this.quality);
    this.bloom = new Bloom();
    this.bloom.applyQuality(this.quality);
    this.lit = makeColorTarget();
    this.composite = new THREE.WebGLRenderTarget(1, 1, {
      type: THREE.HalfFloatType,
      depthBuffer: true,
      depthTexture: makeDepthTexture(),
    });
    this.composite.texture.colorSpace = THREE.NoColorSpace;
    this.copyColorQuad = new FullScreenQuad(
      new THREE.ShaderMaterial({
        uniforms: this.copyColorUniforms,
        vertexShader: COPY_VERT,
        fragmentShader: /* glsl */ `
uniform sampler2D tDiffuse;
varying vec2 vUv;
void main() {
  gl_FragColor = texture2D(tDiffuse, vUv);
}
`,
        depthTest: false,
        depthWrite: false,
        toneMapped: false,
      })
    );
    this.copyDepthQuad = new FullScreenQuad(
      new THREE.ShaderMaterial({
        uniforms: this.copyDepthUniforms,
        vertexShader: COPY_VERT,
        fragmentShader: /* glsl */ `
uniform sampler2D tDepth;
varying vec2 vUv;
void main() {
  gl_FragColor = vec4(0.0);
  gl_FragDepth = texture2D(tDepth, vUv).r;
}
`,
        depthTest: true,
        depthWrite: true,
        depthFunc: THREE.AlwaysDepth,
        colorWrite: false,
        toneMapped: false,
      })
    );
    this.depthOfField = new DepthOfField(this.renderer, this.camera);
    this.depthOfField.applyQuality(this.quality);

    this.scene.add(this.entityGroup);
    this.scene.add(this.overlayGroup);
    this.scene.add(this.viewGrid.group);
    setObjectLayer(this.overlayGroup, LAYER_OVERLAY);
    setObjectLayer(this.viewGrid.group, LAYER_OVERLAY);
    this.viewGrid.attachCollision(this.collision);
    this.setupOverlay();
    this.resize();

    window.addEventListener('resize', () => this.resize());
  }

  get graphicsQuality(): GraphicsQuality {
    return this.preferredQuality;
  }

  get analyticSdfEnabled(): boolean {
    return this.restir.analyticSdfEnabled;
  }

  set analyticSdfEnabled(value: boolean) {
    this.restir.analyticSdfEnabled = value;
    this.restirGi.analyticSdfEnabled = value;
  }

  get analyticBvhEnabled(): boolean {
    return this.restir.analyticBvhEnabled;
  }

  set analyticBvhEnabled(value: boolean) {
    this.restir.analyticBvhEnabled = value;
    this.restirGi.analyticBvhEnabled = value;
  }

  get voxelSpheresEnabled(): boolean {
    return this.restir.voxelSpheresEnabled;
  }

  set voxelSpheresEnabled(value: boolean) {
    this.restir.voxelSpheresEnabled = value;
    this.restirGi.voxelSpheresEnabled = value;
  }

  /** ベンチ用。通常プレイでは呼ばない。 */
  enablePassTiming(enabled = true): void {
    this.passTimingEnabled = enabled;
    if (!enabled) return;
    if (this.passTimer) return;
    const gl = this.renderer.getContext();
    if (gl instanceof WebGL2RenderingContext) this.passTimer = new GpuPassTimer(gl);
  }

  get gpuPassTimer(): GpuPassTimer | null {
    return this.passTimingEnabled ? this.passTimer : null;
  }

  /** メニュー中は低品質にして SDF / ReSTIR を止める。ユーザー設定は変えない。 */
  setMenuLite(enabled: boolean): void {
    if (this.menuLite === enabled) return;
    this.menuLite = enabled;
    this.restir.samplePointLights = !enabled;
    this.applyActiveQuality();
    if (!enabled) this.restirGi.resetHistory();
  }

  /** 描画品質プリセットを適用（影・解像度） */
  setGraphicsQuality(idOrQuality: GraphicsQualityId | GraphicsQuality): void {
    this.preferredQuality =
      typeof idOrQuality === 'string' ? resolveGraphicsQuality(idOrQuality) : idOrQuality;
    this.applyActiveQuality();
  }

  private applyActiveQuality(): void {
    const next = this.menuLite ? GRAPHICS_PRESETS.low : this.preferredQuality;
    if (this.quality === next) return;
    this.quality = next;
    this.applyPixelRatio();
    this.environment.applyQuality(next);
    this.voxelSdf.applyQuality(next);
    this.analyticSdf.invalidate();
    this.restir.applyQuality(next);
    this.restirGi.applyQuality(next);
    this.bloom.applyQuality(next);
    this.depthOfField.applyQuality(next);
    this.resize();
  }

  private applyPixelRatio(): void {
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.quality.pixelRatio));
  }

  /** 外部（アバター等）の Standard マテリアルを G-buffer 用に登録する */
  setupLitMaterial(material: THREE.Material): void {
    this.gbuffer.setupMaterial(material);
  }

  private setupOverlay(): void {
    // 照準中オブジェクトのメッシュ線（非ライティング・影対象外）
    this.highlight = new THREE.LineSegments(
      new THREE.BufferGeometry(),
      new THREE.LineBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.92,
        toneMapped: false,
        depthTest: true,
        depthWrite: false,
      })
    );
    this.highlight.visible = false;
    this.highlight.renderOrder = 3;
    this.overlayGroup.add(this.highlight);
    setObjectLayer(this.overlayGroup, LAYER_OVERLAY);
    setObjectLayer(this.highlight, LAYER_OVERLAY);
  }

  private materialCacheKey(id: string, drawRank: number): string {
    return `${id}:${drawRank}`;
  }

  private loadColorTexture(url: string): THREE.Texture {
    const texture = this.textureLoader.load(url);
    texture.colorSpace = THREE.SRGBColorSpace;
    this.configurePbrTexture(texture);
    return texture;
  }

  private loadDataTexture(url: string): THREE.Texture {
    const texture = this.textureLoader.load(url);
    texture.colorSpace = THREE.NoColorSpace;
    this.configurePbrTexture(texture);
    return texture;
  }

  private configurePbrTexture(texture: THREE.Texture): void {
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.magFilter = THREE.LinearFilter;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
    this.textures.add(texture);
  }

  private pbrMapsFor(entry: PaletteEntry): THREE.MeshStandardMaterialParameters | undefined {
    const urls = materialMapUrls(entry);
    if (!urls) return undefined;
    const existing = this.pbrMaps.get(entry.id);
    if (existing) {
      return {
        map: existing[0],
        normalMap: existing[1],
        roughnessMap: existing[2],
        metalnessMap: existing[3],
        aoMap: existing[4],
      };
    }
    const maps = [
      this.loadColorTexture(urls.map),
      this.loadDataTexture(urls.normalMap),
      this.loadDataTexture(urls.roughnessMap),
      this.loadDataTexture(urls.metalnessMap),
      this.loadDataTexture(urls.aoMap),
    ];
    this.pbrMaps.set(entry.id, maps);
    return {
      map: maps[0],
      normalMap: maps[1],
      roughnessMap: maps[2],
      metalnessMap: maps[3],
      aoMap: maps[4],
    };
  }

  private textureFor(id: string): THREE.Texture {
    const existing = this.materialMaps.get(id);
    if (existing) return existing;
    const custom = this.blockTextures.get(id);
    let texture: THREE.Texture;
    if (custom) {
      const canvas = document.createElement('canvas');
      paintBlockTexture(canvas, custom);
      texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.magFilter = THREE.NearestFilter;
      texture.minFilter = THREE.NearestMipmapLinearFilter;
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
    } else {
      const entry = resolveMaterial(id);
      const seed = Math.max(1, MATERIAL_PALETTE.findIndex((m) => m.id === entry.id) + 1);
      texture = createBlockTexture(seed);
    }
    this.materialMaps.set(id, texture);
    this.textures.add(texture);
    return texture;
  }

  private materialKey(
    id: string,
    drawRank: number,
    lights?: readonly PointLightComponent[]
  ): string {
    const glow = surfaceEmissive(id, lights);
    return glow
      ? `${this.materialCacheKey(id, drawRank)}:${glow.color}:${glow.intensity}`
      : this.materialCacheKey(id, drawRank);
  }

  private materialsFor(
    entity: Entity,
    data: MeshData,
    drawRank: number
  ): THREE.Material | THREE.Material[] {
    const lights = entity.components?.lights;
    if (data.materials && data.materials.length > 0) {
      const materials = data.materials.map((id) =>
        this.materialFor(id, drawRank, isEmissiveMaterial(id) ? lights : undefined)
      );
      return materials.length === 1 ? materials[0] : materials;
    }
    return this.materialFor(entity.material, drawRank, lights);
  }

  private materialFor(
    id: string,
    drawRank = 0,
    lights?: readonly PointLightComponent[]
  ): THREE.Material {
    const key = this.materialKey(id, drawRank, lights);
    const existing = this.materials.get(key);
    if (existing) return existing;

    const glow = surfaceEmissive(id, lights);
    const custom = this.blockTextures.get(id);
    const entry = resolveMaterial(id);
    if (!custom && isWaterMaterial(entry.id)) {
      const material = this.waterMaterial ?? (this.waterMaterial = createWaterMaterial());
      this.materials.set(key, material);
      return material;
    }
    const pbr = custom ? undefined : this.pbrMapsFor(entry);
    const texture = pbr?.map ?? this.textureFor(id);
    const transparent = !custom && isTransparentMaterial(entry.id);
    const material: THREE.Material = transparent
      ? new THREE.MeshBasicMaterial({
          color: custom || pbr ? 0xffffff : entry.color,
          map: texture,
          transparent: true,
          opacity: 0.55,
          depthWrite: false,
          side: THREE.FrontSide,
          toneMapped: false,
        })
      : new THREE.MeshStandardMaterial({
          color: custom || pbr ? 0xffffff : entry.color,
          map: texture,
          normalMap: pbr?.normalMap,
          roughnessMap: pbr?.roughnessMap,
          metalnessMap: pbr?.metalnessMap,
          aoMap: pbr?.aoMap,
          roughness: custom ? 0.88 : entry.roughness,
          metalness: custom ? 0 : entry.metalness,
          emissive: glow ? glow.color : custom ? 0x000000 : (entry.emissive ?? 0x000000),
          emissiveIntensity: glow ? glow.intensity : custom ? 0 : (entry.emissiveIntensity ?? 0),
          side: THREE.DoubleSide,
          toneMapped: false,
        });
    const depthBias = entityDepthBias(drawRank);
    if (depthBias) {
      material.polygonOffset = true;
      material.polygonOffsetFactor = depthBias.factor;
      material.polygonOffsetUnits = depthBias.units;
    }
    if (!custom && entry.tileSize && entry.tileSize !== 1) {
      applyWorldTileSize(material, entry.tileSize);
    }
    if (!transparent) this.gbuffer.setupMaterial(material);
    this.materials.set(key, material);
    return material;
  }

  /** ルーム移動時に、利用可能なカスタムテクスチャをまとめて入れ替える。 */
  setBlockTextures(definitions: readonly BlockTextureDefinition[]): void {
    for (const id of this.blockTextures.keys()) this.disposeCachedMaterial(id);
    this.blockTextures.clear();
    for (const definition of definitions) this.blockTextures.set(definition.id, definition);
  }

  /** 作成通知を受けたテクスチャを、その場でマテリアル解決へ追加する。 */
  setBlockTexture(definition: BlockTextureDefinition): void {
    // 作成直後はoptimisticなレシピとサーバー確定版（createdAt付き）が続けて届く。
    // 同じIDの確定通知では、すでにメッシュが参照中のmaterialを破棄しない。
    if (this.blockTextures.has(definition.id)) {
      this.blockTextures.set(definition.id, definition);
      return;
    }
    this.disposeCachedMaterial(definition.id);
    this.blockTextures.set(definition.id, definition);
  }

  removeBlockTexture(id: string): void {
    this.disposeCachedMaterial(id);
    this.blockTextures.delete(id);
  }

  private disposeCachedMaterial(id: string): void {
    const prefix = `${id}:`;
    for (const key of [...this.materials.keys()]) {
      if (key !== id && !key.startsWith(prefix)) continue;
      this.materials.get(key)?.dispose();
      this.materials.delete(key);
    }
    const map = this.materialMaps.get(id);
    if (map) {
      this.textures.delete(map);
      map.dispose();
      this.materialMaps.delete(id);
    }
    const pbr = this.pbrMaps.get(id);
    if (pbr) {
      for (const texture of pbr) {
        this.textures.delete(texture);
        texture.dispose();
      }
      this.pbrMaps.delete(id);
    }
    this.textureLuminance.delete(id);
  }

  private materialColor(id: string): number {
    return this.blockTextures.get(id)?.color ?? resolveMaterial(id).color;
  }

  private albedoMap(id: string): THREE.Texture | undefined {
    if (this.blockTextures.has(id)) return this.textureFor(id);
    return this.pbrMapsFor(resolveMaterial(id))?.map;
  }

  private surfaceLuminance(id: string): number {
    const cached = this.textureLuminance.get(id);
    if (cached !== undefined) return cached;
    const texture = this.albedoMap(id);
    const sampled = texture ? sampleTextureLuminance(texture) : null;
    if (sampled !== null) {
      this.textureLuminance.set(id, sampled);
      return sampled;
    }
    return hexLuminance(this.materialColor(id));
  }

  /* ---------------------------------------------------------------- */
  /* entity の追加・更新・削除                                         */
  /* ---------------------------------------------------------------- */

  setEntity(entity: Entity): void {
    const previous = this.entities.get(entity.id);
    if (previous && this.hasVisual(entity.id) && entityVisualEqual(previous, entity)) {
      this.entities.set(entity.id, entity);
      this.syncSdfFlags(entity);
      return;
    }
    const transparent =
      !this.blockTextures.has(entity.material) && isTransparentMaterial(entity.material);
    const drawRank = entityDrawRank(entity.type);
    const data = buildEntityMesh(entity);
    const material = this.materialsFor(entity, data, drawRank);
    const existing = this.meshes.get(entity.id);
    if (existing) {
      existing.geometry.dispose();
      existing.geometry = toBufferGeometry(data);
      existing.material = material;
      existing.renderOrder = entityRenderOrder(drawRank, transparent);
      existing.layers.set(transparent ? LAYER_TRANSPARENT : LAYER_LIT);
      this.applyEntityShadowFlags(existing, entity);
    } else {
      const mesh = new THREE.Mesh(toBufferGeometry(data), material);
      mesh.userData.entityId = entity.id;
      mesh.renderOrder = entityRenderOrder(drawRank, transparent);
      mesh.layers.set(transparent ? LAYER_TRANSPARENT : LAYER_LIT);
      this.applyEntityShadowFlags(mesh, entity);
      this.meshes.set(entity.id, mesh);
      this.entityGroup.add(mesh);
    }
    this.entities.set(entity.id, entity);
    this.syncLights(entity);
    if (entity.type === 'object') this.collision.remove(entity.id);
    else this.collision.set(entity);
    this.syncSdfFlags(entity);
    if (this.highlightKey === `entity:${entity.id}` || this.highlightKey?.startsWith(`entity:${entity.id}:`)) {
      this.highlightKey = null;
    }
  }

  private hasVisual(id: string): boolean {
    return this.meshes.has(id);
  }

  private removeMesh(id: string): void {
    const mesh = this.meshes.get(id);
    if (!mesh) return;
    this.entityGroup.remove(mesh);
    mesh.geometry.dispose();
    this.meshes.delete(id);
  }

  private syncSdfFlags(entity: Entity): void {
    if (entity.type === 'object' || skipsSdfOcclusion(entity.material)) this.sdfSkipIds.add(entity.id);
    else this.sdfSkipIds.delete(entity.id);
  }

  private sdfBakeSkipSet(packedIds: readonly string[]): ReadonlySet<string> {
    this.sdfBakeSkipIds.clear();
    for (const id of this.sdfSkipIds) this.sdfBakeSkipIds.add(id);
    for (const id of packedIds) this.sdfBakeSkipIds.add(id);
    return this.sdfBakeSkipIds;
  }

  private applyEntityShadowFlags(mesh: THREE.Mesh, entity: Entity): void {
    mesh.castShadow = entity.type !== 'object';
    mesh.receiveShadow = true;
  }

  /** 形状とは独立した光源コンポーネントを記録する。描画時に視錐台へ届くものだけ ReSTIR へ載せる。 */
  private syncLights(entity: Entity, explicitOrigin?: Vec3): void {
    const configs = entity.components?.lights ?? [];
    if (configs.length === 0) {
      this.lightsByEntity.delete(entity.id);
      return;
    }

    const objectPose = entity.type === 'object' ? entity : null;
    const origin = objectPose
      ? objectPose.geometry.origin
      : (explicitOrigin ?? entityCentroid(entity));
    const bounds = objectPose
      ? objectWorldAabb(
          resolveObjectDefinition(objectPose.definitionId).bounds,
          objectPose.geometry.origin,
          objectPose.geometry.yaw,
          objectPose.geometry.mount ?? '+y'
        )
      : unionBounds(entityCollisionBoxes(entity));
    this.lightsByEntity.set(
      entity.id,
      configs.map((config, index) => {
        const offset = config.offset ?? { x: 0, y: 0, z: 0 };
        const worldOffset = objectPose
          ? objectWorldPoint(
              offset,
              { x: 0, y: 0, z: 0 },
              objectPose.geometry.yaw,
              objectPose.geometry.mount ?? '+y'
            )
          : offset;
        const x = origin.x + worldOffset.x;
        const y = origin.y + worldOffset.y;
        const z = origin.z + worldOffset.z;
        const sourceInsideBounds =
          bounds &&
          x >= bounds.min.x &&
          x <= bounds.max.x &&
          y >= bounds.min.y &&
          y <= bounds.max.y &&
          z >= bounds.min.z &&
          z <= bounds.max.z;
        const sourceRadius = sourceInsideBounds
          ? Math.hypot(
              Math.max(Math.abs(bounds.min.x - x), Math.abs(bounds.max.x - x)),
              Math.max(Math.abs(bounds.min.y - y), Math.abs(bounds.max.y - y)),
              Math.max(Math.abs(bounds.min.z - z), Math.abs(bounds.max.z - z))
            )
          : 0;
        return {
          key: `${entity.id}:${index}`,
          x,
          y,
          z,
          sourceRadius,
          color: config.color,
          intensity: config.intensity,
          range: config.range,
        };
      })
    );
  }

  private collectLightSources(): PointLightSource[] {
    this.camera.updateMatrixWorld();
    this.lightFrustumMatrix.multiplyMatrices(
      this.camera.projectionMatrix,
      this.camera.matrixWorldInverse
    );
    this.lightFrustum.setFromProjectionMatrix(this.lightFrustumMatrix);

    const sources: PointLightSource[] = [];
    for (const lights of this.lightsByEntity.values()) {
      for (const light of lights) {
        this.lightSphere.center.set(light.x, light.y, light.z);
        this.lightSphere.radius = light.range;
        if (this.lightFrustum.intersectsSphere(this.lightSphere)) sources.push(light);
      }
    }
    return sources;
  }

  /** 視錐台に届く点光源を ReSTIR の光源テクスチャへ載せる。残ったランプは前回の index を保つ。 */
  private updateActivePointLights(): void {
    const sources = this.collectLightSources();
    if (sources.length === 0) {
      this.activeLights = [];
      this.activeLightKeys.clear();
      this.restir.setLights([]);
      return;
    }

    const selected = selectNearbyPointLights(
      sources,
      this.camera.position,
      this.quality.maxPointLights,
      {
        far: GROUND_DRAW_DISTANCE,
        previousKeys: this.activeLightKeys,
      }
    );
    this.activeLights = packPointLightsStable(
      selected.map((entry) => entry.source),
      this.activeLights,
      this.quality.maxPointLights
    );
    this.activeLightKeys = new Set(this.activeLights.map((light) => light.key));
    this.restir.setLights(this.activeLights);
  }

  removeEntity(id: string): void {
    this.removeMesh(id);
    this.lightsByEntity.delete(id);
    this.entities.delete(id);
    this.collision.remove(id);
    this.sdfSkipIds.delete(id);
  }

  clearEntities(): void {
    for (const id of [...this.meshes.keys()]) this.removeMesh(id);
    this.lightsByEntity.clear();
    this.activeLights = [];
    this.activeLightKeys.clear();
    this.restir.setLights([]);
    this.entities.clear();
    this.collision.clear();
    this.sdfSkipIds.clear();
  }

  getEntity(id: string): Entity | undefined {
    return this.entities.get(id);
  }

  listEntities(): Entity[] {
    return [...this.entities.values()];
  }

  get entityCount(): number {
    return this.entities.size;
  }

  /* ---------------------------------------------------------------- */
  /* プレビュー・ガイド・照準表示                                      */
  /* ---------------------------------------------------------------- */

  /** 設置時、照準中ブレースを割目のコントラスト格子で縁取る。 */
  setBraceSnapHighlight(entityId: string, snap: number): void {
    const color = this.highlightOutlineColor(entityId);
    const key = `entity:${entityId}:snap:${snap}:c:${color}`;
    if (key === this.highlightKey) {
      this.highlight.visible = true;
      return;
    }
    const entity = this.entities.get(entityId);
    if (!isPathBrace(entity)) {
      this.highlightKey = null;
      this.highlight.visible = false;
      return;
    }
    this.highlightKey = key;
    this.applyHighlightColor(color);
    this.highlight.geometry.dispose();
    this.highlight.geometry = snapFrameGeometry(braceSnapFrameLines(entity.geometry, snap));
    this.highlight.position.set(0, 0, 0);
    this.highlight.scale.set(1, 1, 1);
    this.highlight.visible = true;
  }

  /** 設置時、照準中プレーンを割目のコントラスト格子で縁取る。 */
  setPlaneSnapHighlight(entityId: string, snap: number, focus?: Vec3): void {
    const yaw = this.viewGrid.current.yaw;
    const color = this.highlightOutlineColor(entityId);
    const key = `entity:${entityId}:plane-snap:${snap}:${yaw}${snapFocusKey(focus, snap)}:c:${color}`;
    if (key === this.highlightKey) {
      this.highlight.visible = true;
      return;
    }
    const entity = this.entities.get(entityId);
    if (!isFillBrace(entity)) {
      this.highlightKey = null;
      this.highlight.visible = false;
      return;
    }
    this.highlightKey = key;
    this.applyHighlightColor(color);
    this.highlight.geometry.dispose();
    this.highlight.geometry = snapFrameGeometry(
      planeSnapFrameLines(braceAsSlab(entity.geometry), snap, yaw, focus)
    );
    this.highlight.position.set(0, 0, 0);
    this.highlight.scale.set(1, 1, 1);
    this.highlight.visible = true;
  }

  /** 任意のプレーン（ロックした帯面など）の UV 格子。 */
  setCustomPlaneSnapHighlight(
    geometry: PlaneGeometry,
    snap: number,
    focus?: Vec3,
    entityId?: string
  ): void {
    const yaw = this.viewGrid.current.yaw;
    const color = this.highlightOutlineColor(entityId);
    const key = `custom-plane-snap:${snap}:${yaw}:${JSON.stringify(geometry.points)}${snapFocusKey(focus, snap)}:c:${color}`;
    if (key === this.highlightKey) {
      this.highlight.visible = true;
      return;
    }
    this.highlightKey = key;
    this.applyHighlightColor(color);
    this.highlight.geometry.dispose();
    this.highlight.geometry = snapFrameGeometry(planeSnapFrameLines(geometry, snap, yaw, focus));
    this.highlight.position.set(0, 0, 0);
    this.highlight.scale.set(1, 1, 1);
    this.highlight.visible = true;
  }

  /** 破壊は赤、それ以外はテクスチャの明暗で白／黒。グローバルグリッドのセルだけ緑。 */
  setHighlight(
    entityId: string | null,
    gridCell?: GridCell,
    segmentIndex?: number,
    destroy = false
  ): void {
    const color = gridCell
      ? GRID_HIGHLIGHT_COLOR
      : this.highlightOutlineColor(entityId, destroy);
    const key = highlightTargetKey(entityId, gridCell, this.viewGrid.current, segmentIndex, color);
    if (key === this.highlightKey) {
      this.highlight.visible = key !== null;
      return;
    }
    this.highlightKey = key;
    if (!key) {
      this.highlight.visible = false;
      return;
    }

    this.applyHighlightColor(color);
    if (gridCell) {
      this.highlight.geometry.dispose();
      this.highlight.geometry = gridCellGeometry(gridCell, this.viewGrid.current);
      this.highlight.position.set(0, 0, 0);
      this.highlight.scale.set(1, 1, 1);
      this.highlight.visible = true;
      return;
    }
    const entity = entityId ? this.entities.get(entityId) : undefined;
    if (segmentIndex !== undefined) {
      const slice = isFillBrace(entity)
        ? planeLogicalSegment(braceAsSlab(entity.geometry), segmentIndex)
        : isPathBrace(entity)
          ? braceLogicalSegment(entity.geometry, segmentIndex)
          : null;
      if (slice) {
        const geometry = toBufferGeometry(buildBraceMesh(slice));
        this.applyHighlightGeometry(geometry);
        geometry.dispose();
        return;
      }
    }
    const source = entityId ? this.meshes.get(entityId)?.geometry ?? null : null;
    if (!source) {
      this.highlightKey = null;
      this.highlight.visible = false;
      return;
    }
    this.applyHighlightGeometry(source);
  }

  private highlightOutlineColor(entityId: string | null | undefined, destroy = false): number {
    if (destroy) return DESTROY_HIGHLIGHT_COLOR;
    if (!entityId) return OUTLINE_LIGHT;
    const entity = this.entities.get(entityId);
    if (!entity) return OUTLINE_LIGHT;
    return contrastOutlineFromLuminance(this.surfaceLuminance(entity.material));
  }

  private applyHighlightColor(color: number): void {
    (this.highlight.material as THREE.LineBasicMaterial).color.setHex(color);
  }

  private applyHighlightGeometry(source: THREE.BufferGeometry): void {
    this.highlight.geometry.dispose();
    this.highlight.geometry = new THREE.EdgesGeometry(source, 18);
    this.padOutlineGeometry(this.highlight.geometry);
    this.highlight.position.set(0, 0, 0);
    this.highlight.scale.set(1, 1, 1);
    this.highlight.visible = true;
  }

  /** メッシュのハードエッジを少し膨らませて、面との Z ファイティングを避ける。 */
  private padOutlineGeometry(geometry: THREE.BufferGeometry, pad = 0.004): void {
    geometry.computeBoundingBox();
    const box = geometry.boundingBox;
    if (!box) return;
    const cx = (box.min.x + box.max.x) / 2;
    const cy = (box.min.y + box.max.y) / 2;
    const cz = (box.min.z + box.max.z) / 2;
    const hx = Math.max((box.max.x - box.min.x) / 2, 1e-6);
    const hy = Math.max((box.max.y - box.min.y) / 2, 1e-6);
    const hz = Math.max((box.max.z - box.min.z) / 2, 1e-6);
    const pos = geometry.getAttribute('position');
    for (let i = 0; i < pos.count; i++) {
      pos.setXYZ(
        i,
        cx + ((pos.getX(i) - cx) / hx) * (hx + pad),
        cy + ((pos.getY(i) - cy) / hy) * (hy + pad),
        cz + ((pos.getZ(i) - cz) / hz) * (hz + pad)
      );
    }
    pos.needsUpdate = true;
  }

  /** 配置確定前の形状プレビュー（影対象外・非ライティングで視認性を維持） */
  setPreview(
    entity: Entity | null,
    pose?: BlockPose,
    color?: number,
    overlays: readonly Entity[] = [],
    style: 'solid' | 'frame' = 'solid'
  ): void {
    const key = previewCacheKey(entity, pose, color, style);
    const overlayKey = previewOverlayKey(overlays, color);
    if (key !== this.previewKey) {
      this.previewKey = key;
      this.clearPreviewMesh();
      if (entity || pose) {
        this.previewMesh =
          style === 'frame'
            ? this.createPreviewFrame(entity, pose, color)
            : this.createPreviewMesh(entity, pose, color);
      }
    }
    if (overlayKey !== this.previewOverlayKey) {
      this.previewOverlayKey = overlayKey;
      this.clearPreviewOverlays();
      for (const overlay of overlays) {
        const mesh = this.createPreviewMesh(overlay, undefined, color, true);
        if (mesh) this.previewOverlays.push(mesh);
      }
    }
  }

  private createPreviewMesh(
    entity: Entity | null,
    pose?: BlockPose,
    color?: number,
    overlay = false
  ): THREE.Mesh | null {
    const data = pose ? buildBlockMesh(pose) : entity ? buildEntityMesh(entity) : null;
    if (!data || data.indices.length === 0) return null;
    const material = overlay ? this.ensurePreviewOverlayMaterial() : this.ensurePreviewMaterial();
    material.color.setHex(color ?? (entity ? this.materialColor(entity.material) : 0xffffff));
    // 既存面と重なると Z ファイティングでちらつくので、重心まわりに約 1px 分だけ内側へ縮める
    const inset = 0.002;
    const center = pose ? pose.origin : entity ? entityCentroid(entity) : { x: 0, y: 0, z: 0 };
    const geometry = toBufferGeometry(data);
    geometry.translate(-center.x, -center.y, -center.z);
    geometry.computeBoundingBox();
    geometry.boundingBox!.getSize(this.previewSize);
    const scaleAxis = (length: number) =>
      length > 1e-6 ? Math.max(0, length - inset * 2) / length : 1;
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(center.x, center.y, center.z);
    mesh.scale.set(
      scaleAxis(this.previewSize.x),
      scaleAxis(this.previewSize.y),
      scaleAxis(this.previewSize.z)
    );
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    this.overlayGroup.add(mesh);
    setObjectLayer(mesh, LAYER_OVERLAY);
    return mesh;
  }

  private createPreviewFrame(
    entity: Entity | null,
    pose?: BlockPose,
    color?: number
  ): THREE.LineSegments | null {
    const data = pose ? buildBlockMesh(pose) : entity ? buildEntityMesh(entity) : null;
    if (!data || data.indices.length === 0) return null;
    const source = toBufferGeometry(data);
    const geometry = new THREE.EdgesGeometry(source, 18);
    source.dispose();
    this.padOutlineGeometry(geometry);
    if (!this.previewFrameMaterial) {
      this.previewFrameMaterial = new THREE.LineBasicMaterial({
        transparent: true,
        opacity: 0.92,
        depthTest: false,
        depthWrite: false,
        toneMapped: false,
      });
    }
    this.previewFrameMaterial.color.setHex(color ?? (entity ? this.materialColor(entity.material) : 0xffffff));
    const frame = new THREE.LineSegments(geometry, this.previewFrameMaterial);
    frame.renderOrder = 3;
    frame.castShadow = false;
    frame.receiveShadow = false;
    this.overlayGroup.add(frame);
    setObjectLayer(frame, LAYER_OVERLAY_LINES);
    return frame;
  }

  private ensurePreviewMaterial(): THREE.MeshBasicMaterial {
    if (!this.previewMaterial) {
      this.previewMaterial = new THREE.MeshBasicMaterial({
        transparent: true,
        opacity: 0.28,
        depthWrite: false,
        side: THREE.DoubleSide,
        toneMapped: false,
      });
    }
    return this.previewMaterial;
  }

  private ensurePreviewOverlayMaterial(): THREE.MeshBasicMaterial {
    if (!this.previewOverlayMaterial) {
      this.previewOverlayMaterial = new THREE.MeshBasicMaterial({
        transparent: true,
        opacity: 0.45,
        depthWrite: false,
        side: THREE.DoubleSide,
        toneMapped: false,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -2,
      });
    }
    return this.previewOverlayMaterial;
  }

  private clearPreviewMesh(): void {
    if (!this.previewMesh) return;
    this.overlayGroup.remove(this.previewMesh);
    this.previewMesh.geometry.dispose();
    this.previewMesh = null;
  }

  private clearPreviewOverlays(): void {
    for (const mesh of this.previewOverlays) {
      this.overlayGroup.remove(mesh);
      mesh.geometry.dispose();
    }
    this.previewOverlays = [];
  }

  /** 作図中の点列（クリック済みの点 + 現在の照準位置）を表示する */
  setGuide(points: Vec3[], cursor: Vec3 | null, closed = false, markerSize = GUIDE_MARKER_SIZE): void {
    const key = guideCacheKey(points, cursor, closed, markerSize);
    if (key === this.guideKey) return;
    this.guideKey = key;

    const specs: { point: Vec3; cursor: boolean }[] = points.map((point) => ({
      point,
      cursor: false,
    }));
    if (cursor) specs.push({ point: cursor, cursor: true });
    const markerScale = markerSize / GUIDE_MARKER_SIZE;

    while (this.guideMarkers.length < specs.length) {
      const marker = new THREE.Mesh(this.guideBox, this.guidePointMaterial);
      marker.renderOrder = 2;
      marker.castShadow = false;
      marker.receiveShadow = false;
      this.guideMarkers.push(marker);
      this.overlayGroup.add(marker);
      setObjectLayer(marker, LAYER_OVERLAY);
    }
    for (let i = 0; i < this.guideMarkers.length; i++) {
      const marker = this.guideMarkers[i];
      const spec = specs[i];
      if (!spec) {
        marker.visible = false;
        continue;
      }
      marker.visible = true;
      marker.material = spec.cursor ? this.guideCursorMaterial : this.guidePointMaterial;
      marker.position.set(spec.point.x, spec.point.y, spec.point.z);
      marker.scale.set(markerScale, markerScale, markerScale);
    }

    const linePoints = points.map((point) => new THREE.Vector3(point.x, point.y, point.z));
    if (cursor) linePoints.push(new THREE.Vector3(cursor.x, cursor.y, cursor.z));
    if (closed && linePoints.length > 2) linePoints.push(linePoints[0].clone());
    if (linePoints.length >= 2) {
      if (!this.guideLine) {
        this.guideLine = new THREE.Line(new THREE.BufferGeometry(), this.guideLineMaterial);
        this.guideLine.renderOrder = 2;
        this.overlayGroup.add(this.guideLine);
        setObjectLayer(this.guideLine, LAYER_OVERLAY);
      }
      this.guideLine.geometry.dispose();
      this.guideLine.geometry = new THREE.BufferGeometry().setFromPoints(linePoints);
      this.guideLine.visible = true;
    } else if (this.guideLine) {
      this.guideLine.visible = false;
    }
  }

  /* ---------------------------------------------------------------- */
  /* ピッキング                                                        */
  /* ---------------------------------------------------------------- */

  /** クロスヘア（画面中央 = カメラ正面）から届く範囲でレイキャストする */
  pickFromCamera(reach: number, opaqueOnly = false): PickResult | null {
    if (opaqueOnly) this.raycaster.layers.set(LAYER_LIT);
    else this.raycaster.layers.enableAll();
    this.camera.getWorldPosition(this.pickOrigin);
    this.camera.getWorldDirection(this.pickDirection);
    this.raycaster.set(this.pickOrigin, this.pickDirection);
    this.raycaster.far = reach;

    const hits = this.raycaster.intersectObjects(this.entityGroup.children, false);
    const entityHit = hits[0];
    this.pickOriginVec.x = this.pickOrigin.x;
    this.pickOriginVec.y = this.pickOrigin.y;
    this.pickOriginVec.z = this.pickOrigin.z;
    this.pickDirectionVec.x = this.pickDirection.x;
    this.pickDirectionVec.y = this.pickDirection.y;
    this.pickDirectionVec.z = this.pickDirection.z;
    const gridHit = this.pickGrid(this.pickOriginVec, this.pickDirectionVec, reach);

    if (entityHit && (!gridHit || entityHit.distance <= gridHit.distance)) {
      worldNormalFromHit(entityHit, this.pickNormal);
      if (this.pickNormal.dot(this.pickDirection) > 0) this.pickNormal.negate();
      return {
        point: { x: entityHit.point.x, y: entityHit.point.y, z: entityHit.point.z },
        normal: { x: this.pickNormal.x, y: this.pickNormal.y, z: this.pickNormal.z },
        entityId: this.entityIdFromHit(entityHit),
        distance: entityHit.distance,
      };
    }

    return gridHit;
  }

  private entityIdFromHit(hit: THREE.Intersection): string | null {
    return (hit.object.userData.entityId as string) ?? null;
  }

  private pickGrid(origin: Vec3, direction: Vec3, reach: number): PickResult | null {
    const hit = intersectGridPlane(origin, direction, this.viewGrid.current, reach);
    if (!hit) return null;
    return {
      point: hit.point,
      normal: { x: 0, y: hit.side === 'above' ? 1 : -1, z: 0 },
      entityId: null,
      gridCell: hit.cell,
      gridSide: hit.side,
      distance: hit.distance,
    };
  }

  /* ---------------------------------------------------------------- */
  /* レンダリング                                                      */
  /* ---------------------------------------------------------------- */

  setOccluderBoxes(boxes: readonly Aabb[]): void {
    this.occluderBoxes = boxes.slice();
  }

  private resize(): void {
    const width = window.innerWidth;
    const height = window.innerHeight;
    this.applyPixelRatio();
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    const drawing = this.renderer.getDrawingBufferSize(new THREE.Vector2());
    this.gbuffer.setSize(drawing.x, drawing.y);
    this.lit.setSize(drawing.x, drawing.y);
    this.composite.setSize(drawing.x, drawing.y);
    this.restir.setSize(drawing.x, drawing.y);
    this.restirGi.setSize(drawing.x, drawing.y);
    this.bloom.setSize(drawing.x, drawing.y);
    this.depthOfField.setSize(width, height, this.renderer.getPixelRatio());
    this.environment.updateFrustums();
  }

  /** カメラを目の位置・向きに合わせる */
  setCamera(eye: Vec3, yaw: number, pitch: number): void {
    this.camera.position.set(eye.x, eye.y, eye.z);
    this.camera.rotation.set(pitch, yaw, 0, 'YXZ');
  }

  /** 経過時間を受け取り、G-buffer → ReSTIR GI → ReSTIR DI → デノイズ → bloom → オーバーレイ → DoF で描く。 */
  render(elapsed = 0): void {
    const dt =
      this.lastRenderElapsed === 0 ? 1 / 60 : Math.min(elapsed - this.lastRenderElapsed, 0.25);
    this.lastRenderElapsed = elapsed;
    if (this.collisionDebug) {
      this.collisionDebugDraw.sync(this.collision);
      const previousToneMapping = this.renderer.toneMapping;
      const previousAutoClear = this.renderer.autoClear;
      this.renderer.toneMapping = THREE.NoToneMapping;
      this.renderer.autoClear = true;
      this.renderer.setRenderTarget(null);
      this.renderer.render(this.collisionDebugDraw.scene, this.camera);
      this.renderer.toneMapping = previousToneMapping;
      this.renderer.autoClear = previousAutoClear;
      return;
    }
    if (this.menuLite) {
      if (this.activeLights.length > 0) {
        this.activeLights = [];
        this.activeLightKeys.clear();
        this.restir.setLights([]);
      }
    } else {
      this.updateActivePointLights();
    }
    this.environment.sunEnabled = this.restir.sunEnabled;
    this.environment.update(elapsed, this.camera);
    const sdfEnabled =
      !this.menuLite &&
      (this.restir.sunSdfShadowsEnabled ||
        this.restir.diSdfShadowsEnabled ||
        this.restirGi.sdfShadowsEnabled);
    const giEnabled = !this.menuLite && this.restir.ambientEnabled;
    const timer = this.passTimingEnabled ? this.passTimer : null;
    timer?.poll();
    if (sdfEnabled && (this.analyticSdfEnabled || this.voxelSpheresEnabled)) {
      // 原点スナップは体積の平行移動だけなので、ReSTIR / デノイザの履歴は捨てない。
      timer?.beginCpu('sdfRebuild');
      const packedIds = this.analyticSdfEnabled
        ? this.analyticSdf.rebuild(
            this.entities.values(),
            this.sdfSkipIds,
            volumeFromCenter(this.camera.position, this.voxelSdf.extent, this.voxelSdf.resolution),
            this.collision.revision
          )
        : [];
      if (this.voxelSpheresEnabled) {
        const leftover = this.analyticSdfEnabled ? this.analyticSdf.leftover : null;
        this.voxelSdf.rebuild(
          this.collision,
          this.occluderBoxes,
          this.camera.position,
          this.sdfBakeSkipSet(leftover ? [...packedIds, ...leftover.ids] : packedIds),
          leftover
            ? {
                planes: leftover.planes,
                triangles: leftover.triangles,
                braces: leftover.braces,
              }
            : {}
        );
      }
      timer?.endCpu('sdfRebuild');
    }

    const previousToneMapping = this.renderer.toneMapping;
    const previousAutoClear = this.renderer.autoClear;
    this.renderer.toneMapping = THREE.NoToneMapping;
    timer?.beginFrame();

    this.gbuffer.passUniform.value = 1;
    this.camera.layers.set(LAYER_LIT);
    this.renderer.setRenderTarget(this.gbuffer.target);
    this.renderer.autoClear = true;
    timePass(timer, 'gbuffer', () => {
      this.renderer.render(this.scene, this.camera);
    });

    if (giEnabled) {
      this.restirGi.render(
        this.camera,
        this.gbuffer,
        this.voxelSdf,
        this.analyticSdf,
        this.restir.lightMap,
        this.restir.lightCount,
        timer
      );
    } else {
      this.restirGi.resetHistory();
    }

    this.restir.render(
      this.camera,
      this.gbuffer,
      this.voxelSdf,
      this.analyticSdf,
      this.lit,
      this.environment,
      giEnabled ? this.restirGi.irradiance : null,
      timer
    );
    if (giEnabled) {
      this.restirGi.captureLighting(this.lit.texture, this.gbuffer.depth, this.camera);
    }
    const bloomed = timePassValue(timer, 'bloom', () => this.bloom.apply(this.renderer, this.lit.texture));

    this.renderer.setRenderTarget(this.composite);
    this.renderer.autoClear = true;
    this.copyColorUniforms.tDiffuse.value = bloomed;
    this.copyColorQuad.render(this.renderer);
    this.copyColorUniforms.tDiffuse.value = null;

    this.renderer.autoClear = false;
    this.copyDepthUniforms.tDepth.value = this.gbuffer.depth;
    this.copyDepthQuad.render(this.renderer);
    this.copyDepthUniforms.tDepth.value = null;

    if (this.waterMaterial) {
      this.renderer.getDrawingBufferSize(this.drawingSize);
      updateWaterMaterial(this.waterMaterial, {
        sceneColor: bloomed,
        sceneDepth: this.gbuffer.depth,
        camera: this.camera,
        width: this.drawingSize.x,
        height: this.drawingSize.y,
        time: elapsed,
        sunEnabled: this.environment.sunEnabled,
      });
    }

    this.gbuffer.passUniform.value = 0;
    timePass(timer, 'overlay', () => {
      this.camera.layers.set(LAYER_OVERLAY);
      this.camera.layers.enable(LAYER_TRANSPARENT);
      this.renderer.setRenderTarget(this.composite);
      this.renderer.render(this.scene, this.camera);

      const savedNear = this.camera.near;
      this.camera.near = Math.min(savedNear, 0.005);
      this.camera.updateProjectionMatrix();
      this.camera.layers.set(LAYER_OVERLAY_LINES);
      this.renderer.render(this.scene, this.camera);
      this.camera.near = savedNear;
      this.camera.updateProjectionMatrix();
    });

    this.camera.layers.enableAll();
    this.renderer.toneMapping = previousToneMapping;
    this.renderer.autoClear = previousAutoClear;
    this.renderer.autoClearColor = true;
    this.renderer.autoClearDepth = true;
    this.renderer.setRenderTarget(null);

    const focusHit = this.pickFromCamera(GROUND_DRAW_DISTANCE, true);
    const dofEnabled = this.depthOfField.enabled;
    if (this.menuLite) this.depthOfField.enabled = false;
    timePass(timer, 'dof', () => {
      this.depthOfField.compose(this.composite.texture, this.gbuffer.depth, focusHit?.distance ?? null, dt);
    });
    this.depthOfField.enabled = dofEnabled;
    timer?.endFrame();
  }
}
